import { useState, useEffect } from 'react'
import { Button } from '@/components/catalyst-ui/button'
import { Heading } from '@/components/catalyst-ui/heading'
import { Text } from '@/components/catalyst-ui/text'
import { Input, InputGroup } from '@/components/catalyst-ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/catalyst-ui/table'
import { Badge } from '@/components/catalyst-ui/badge'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { useListUserDomains, useWorkspaceDomainsWithNs, useAddDomainWithNs, useCheckDomainWithNsStatus, useDisconnectDomainWithNsFromProject, useConnectUserDomainToProject, useDisconnectThirdPartyDomain } from '../hooks/useMyDomains'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '@/components/catalyst-ui/dialog'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useActiveWorkspace } from '@/state/selection.store'
import { showSimpleSuccess, showSimpleError } from '@/lib/toast'
import ProjectAlreadyConnectedDialog from '@/features/domains/components/ProjectAlreadyConnectedDialog'
import { useProjects } from '@/features/workspace/hooks/useProjects'
import { useQueryClient } from '@tanstack/react-query'
import { GlobeIcon16 } from '@/assets/icons'
import { usePermissions } from '@/hooks/usePermissions'
import { hasFeature } from '@/lib/hasFeature'
import { useSelectionStore } from '@/state/selection.store'

export default function DomainConnectionPage() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const domainName = searchParams.get('domain')
  const workspaceIdFromUrl = searchParams.get('workspace')
  const projectIdFromUrl = searchParams.get('project')
  const domainTypeFromUrl = searchParams.get('domainType') || 'withNS'
  const { user } = useAuth()
  const { hasRole } = usePermissions()
  const isViewer = hasRole('viewer')
  
  // Dialog state
  const [showAlreadyConnectedDialog, setShowAlreadyConnectedDialog] = useState(false)
  const { workspaceId: activeWorkspaceId } = useActiveWorkspace()
  
  // Use workspace ID from URL if available, otherwise fall back to active workspace
  const workspaceId = workspaceIdFromUrl && workspaceIdFromUrl !== 'default' ? workspaceIdFromUrl : activeWorkspaceId
  
  // Fetch projects to get project details
  const { projects } = useProjects(workspaceId)
  const currentProject = projects?.find(p => String(p._id || p.id) === String(projectIdFromUrl))
  
  // State for domain info
  const [domainInfo, setDomainInfo] = useState(null)
  const [domainStatus, setDomainStatus] = useState('Not Connected')
  
  // Fetch workspace domains with NS (for domains registered via CodeDesign)
  const { data: wsDomainsData, isLoading: isLoadingWsDomains, isError: isWsDomainsError, error: wsDomainsError } = useWorkspaceDomainsWithNs(workspaceId)
  
  // Fetch user domains (for third-party domains)
  const { data: userDomainsData, isLoading: isLoadingUserDomains, isError: isUserDomainsError, error: userDomainsError } = useListUserDomains(user?.id, { workspaceId })
  
  // Mutations
  const addDomainMutation = useAddDomainWithNs()
  const checkStatusMutation = useCheckDomainWithNsStatus(false)
  const disconnectMutation = useDisconnectDomainWithNsFromProject()
  const disconnectThirdPartyMutation = useDisconnectThirdPartyDomain()
  const connectUserDomainMutation = useConnectUserDomainToProject()
  const queryClient = useQueryClient()
  
  // Find the specific domain
  useEffect(() => {
    if (domainName) {
      // Check in workspace domains first (domains with NS)
      if (wsDomainsData?.data) {
        const foundWsDomain = wsDomainsData.data.find(domain => 
          String(domain.name || domain.domain_name || domain.domain).toLowerCase() === String(domainName).toLowerCase()
        )
        
        if (foundWsDomain) {
          setDomainInfo({
            ...foundWsDomain,
            domainType: 'withNS',
            name: foundWsDomain.name || foundWsDomain.domain_name,
          })
          
          // Check if connected to any project (not just the one from URL)
          const isConnected = !!foundWsDomain.project_id
          setDomainStatus(isConnected ? 'Connected Successfully' : 'Not Connected')
          return
        }
      }
      
      // Check in user domains (third-party domains)
      if (userDomainsData?.data) {
        const foundUserDomain = userDomainsData.data.find(domain => 
          String(domain.domain_name || domain.name || domain.domain).toLowerCase() === String(domainName).toLowerCase()
        )
        
        if (foundUserDomain) {
          // Get project ID for third-party domain
          let projectId = null;
          if (foundUserDomain.project) {
            projectId = typeof foundUserDomain.project === 'string' 
              ? foundUserDomain.project 
              : (foundUserDomain.project?._id || foundUserDomain.project?.id);
          }
          
          const isConnected = !!projectId
          setDomainInfo({
            ...foundUserDomain,
            domainType: 'thirdParty',
            name: foundUserDomain.domain_name || foundUserDomain.name || foundUserDomain.domain,
            project_id: projectId, // Set project_id for consistency
            project: projectId
          })
          setDomainStatus(isConnected ? 'Connected Successfully' : 'Not Connected')
        }
      }
    }
  }, [wsDomainsData, userDomainsData, domainName, projectIdFromUrl])

  const handleConnectSite = async () => {
    if (!domainInfo || !currentProject) {
      showSimpleError('Missing domain or project information')
      return
    }

    // License check: if the project isn't licensed, show pricing/license assignment modal
    if (!currentProject?.order?.paid) {
      const normalizedProject = { ...currentProject, id: currentProject.id || currentProject._id }
      useSelectionStore.getState().setCurrentProject(normalizedProject)
      hasFeature('DOMAIN_CONNECTION', () => {
        // License assigned — proceed with connection
        proceedWithConnection()
      }, ['limitPremium'])
      return
    }

    proceedWithConnection()
  }

  const proceedWithConnection = async () => {
    
    // Get current domain's project ID
    let currentDomainProjectId = null;
    if (domainInfo.project_id) {
      currentDomainProjectId = domainInfo.project_id;
    } else if (domainInfo.project) {
      currentDomainProjectId = typeof domainInfo.project === 'string' 
        ? domainInfo.project 
        : (domainInfo.project?._id || domainInfo.project?.id);
    }
    
    // Check if already connected to this project
    const isAlreadyConnected = currentDomainProjectId && String(currentDomainProjectId) === String(projectIdFromUrl)
    
    if (isAlreadyConnected) {
      setShowAlreadyConnectedDialog(true)
      return
    }
    
    // Check if project already has another domain connected (withNS or third-party)
    const allWsDomains = wsDomainsData?.data || []
    const allUserDomains = userDomainsData?.data || []
    
    // Find if project has any other domain connected
    const projectHasOtherDomain = [...allWsDomains, ...allUserDomains].some(d => {
      let dProjectId = null;
      if (d.project_id) {
        dProjectId = d.project_id;
      } else if (d.project) {
        dProjectId = typeof d.project === 'string' ? d.project : (d.project?._id || d.project?.id);
      }
      
      const dName = d.name || d.domain_name || d.domain;
      return dProjectId && 
             String(dProjectId) === String(projectIdFromUrl) && 
             String(dName).toLowerCase() !== String(domainName).toLowerCase();
    })
    
    if (projectHasOtherDomain) {
      setShowAlreadyConnectedDialog(true)
      return
    }
    
    // Use domainInfo.domainType (detected from actual API data) as primary source
    // domainTypeFromUrl defaults to 'withNS' and is unreliable
    const effectiveDomainType = domainInfo.domainType || domainTypeFromUrl

    try {
      if (effectiveDomainType === 'withNS') {
        // For domains with NS, use the addDomainWithNs mutation to connect
        await addDomainMutation.mutateAsync({
          domainData: {
            name: domainInfo.name,
            expiry_date: domainInfo.expiry_date || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          },
          project: currentProject,
          workspaceId: workspaceId
        })
        
        showSimpleSuccess(`Domain ${domainInfo.name} connected to ${currentProject.name}`)
        setDomainStatus('Connected Successfully')
        
        // Refresh queries
        queryClient.invalidateQueries(['workspaceDomainsWithNs'])
        queryClient.invalidateQueries(['userDomainsByScope'])
        queryClient.invalidateQueries(['userDomains'])
        
        // withNS domains have DNS managed by CodeDesign — no manual verification needed
        // Navigate back to project domain settings
        setTimeout(() => {
          navigate(`/domain?workspace=${workspaceId}&project=${projectIdFromUrl}`)
        }, 1500)
      } else {
        // For third-party domains, use connectUserDomainToProject
        await connectUserDomainMutation.mutateAsync({
          userId: user?.id,
          domain: domainInfo.name,
          project: currentProject.uniqueUrl || currentProject.name
        })
        
        showSimpleSuccess(`Domain ${domainInfo.name} connected to ${currentProject.name}`)
        setDomainStatus('Connected Successfully')
        
        // Refresh queries
        queryClient.invalidateQueries(['userDomainsByScope'])
        queryClient.invalidateQueries(['userDomains'])
        queryClient.invalidateQueries(['workspaceDomainsWithNs'])
        queryClient.invalidateQueries(['thirdPartyDomainDetails'])
        
        // Navigate to records page — it checks verification status and shows
        // DNS records if verification was reset (project type change), or
        // redirects to config if already verified
        setTimeout(() => {
          navigate(`/domains/my-domain/records?domain=${encodeURIComponent(domainInfo.name)}&workspace=${workspaceId}`)
        }, 1500)
      }
    } catch (error) {
      console.error('Failed to connect site:', error)
      showSimpleError(`Failed to connect site: ${error.message}`)
    }
  }

  const handleDisconnectDomain = async () => {
    if (!domainInfo) return;
    
    try {
      const effectiveType = domainInfo.domainType || domainTypeFromUrl
      if (effectiveType === 'thirdParty') {
        // For third-party domains
        await disconnectThirdPartyMutation.mutateAsync({
          userId: user?.id,
          domain: domainInfo.name
        });
        
        showSimpleSuccess(`Domain ${domainInfo.name} disconnected from project`);
        queryClient.invalidateQueries(['userDomainsByScope']);
        queryClient.invalidateQueries(['thirdPartyDomainDetails']);
      } else {
        // For withNS domains
        await disconnectMutation.mutateAsync({
          workspaceId,
          domain: domainInfo.name
        });
        
        showSimpleSuccess(`Domain ${domainInfo.name} disconnected from project`);
        queryClient.invalidateQueries(['workspaceDomainsWithNs']);
      }
      
      // Refresh all domain queries
      queryClient.invalidateQueries(['workspaceDomainsWithNs']);
      queryClient.invalidateQueries(['userDomainsByScope']);
      
      // Navigate back to domain settings
      navigate(`/domain?workspace=${workspaceId}&project=${projectIdFromUrl}`)
    } catch (error) {
      console.error('Failed to disconnect domain:', error);
      showSimpleError(`Failed to disconnect domain: ${error.message}`);
    }
  }

  // Loading state
  const isLoading = isLoadingWsDomains || isLoadingUserDomains
  if (isLoading) {
    return (
      <div className="max-w-[1200px] mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading domain information...</div>
        </div>
      </div>
    )
  }

  // Error state
  const isError = isWsDomainsError || isUserDomainsError
  const error = wsDomainsError || userDomainsError
  if (isError) {
    return (
      <div className="max-w-[1200px] mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">
            Error loading domain: {error?.message}
          </div>
        </div>
      </div>
    )
  }

  // Default domain info if not found
  const defaultDomainInfo = {
    name: domainName || 'Unknown Domain',
    type: domainInfo?.domainType === 'withNS' ? 'Domain with Nameservers' : 'Third-party Domain',
    validation: {
      status: domainStatus === 'Connected Successfully' ? 'Validated Successfully' : 'Pending Validation',
      description:
        'Update your DNS Record. By default, Codedesign will provide the required DNS Records in order for your domain to resolve to our servers. You can view them here',
    },
    records: [
      { label: 'Domain', value: domainName || 'Unknown' },
      { label: 'Expiry date', value: domainInfo?.expiry_date ? new Date(domainInfo.expiry_date).toLocaleDateString() : '-' },
      { label: 'Connect date', value: domainInfo?.created_at ? new Date(domainInfo.created_at).toLocaleDateString() : new Date().toLocaleString() },
      { label: 'Registrar', value: domainInfo?.domainType === 'withNS' ? 'CodeDesign' : 'Third Party' },
      { label: 'Project', value: currentProject?.name || 'Not connected' },
      { label: 'DNS Record', value: domainInfo?.dnsVerified ? 'Verified' : 'Not Verified' },
    ],
  }

  const currentDomainInfo = domainInfo ? {
    ...defaultDomainInfo,
    name: domainInfo.name || domainName,
    type: domainInfo.domainType === 'withNS' ? 'Domain with Nameservers' : 'Third-party Domain',
  } : defaultDomainInfo

  // Format connect date
  const formatConnectDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${day} ${month} ${year} ${displayHours}:${displayMinutes} ${ampm}`;
  }

  return (
    <div className="space-y-6 overflow-x-hidden flex flex-col items-center justify-center">
      {/* Header */}
      <div className="max-w-[896px] w-full mx-auto">
        <div className="flex items-center justify-between border-b border-gray-200 pb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Domain Settings</h1>
          </div>
        </div>
      </div>

      {/* Domain Section */}
      <div className="items-center justify-center mx-auto space-y-3 w-full">
        <div className="flex items-center justify-between max-w-[432px] w-full mx-auto">
          <Heading level={3} className="text-lg font-medium text-gray-900">
            Domain
          </Heading>
          {domainStatus === 'Connected Successfully' ? (
            <Badge color="lime" className="text-sm font-medium">
              Connected Successfully
            </Badge>
          ) : (
            <Badge color="zinc" className="text-sm font-medium">
              Not Connected
            </Badge>
          )}
        </div>

        <Text className="text-sm !font-normal max-w-[432px] w-full mx-auto !text-[#71717B] leading-relaxed">
          Update your DNS Record By default, Codedesign will provide the required DNS Records in order for your domain to resolve to our servers. You can view them here
        </Text>

        {/* Domain Input Field */}
        <div className="flex items-center gap-2 max-w-[432px] w-full mx-auto border border-[#09090B1A] shadow-xs rounded-lg p-2">
          
          <GlobeIcon16 data-slot="icon" />
          <div className="w-full text-sm font-normal text-[#99A1AF]">
              {domainInfo?.name || domainName || ''}
          </div>
        </div>

        {/* Action Buttons */}
        {!isViewer && (
          domainStatus === 'Connected Successfully' ? (
            <div className="flex flex-col sm:flex-row max-w-[432px] w-full mx-auto sm:items-center justify-between gap-2 mt-8">
              <Text className="text-sm text-[#71717B] whitespace-nowrap">Don't want this domain</Text>
              <Button
                onClick={handleDisconnectDomain}
                className="hover:!bg-[#B91C1C]/10 text-sm whitespace-nowrap font-normal !text-[#E7000B]"
                outline
                disabled={disconnectMutation.isPending || disconnectThirdPartyMutation.isPending}
              >
                {(disconnectMutation.isPending || disconnectThirdPartyMutation.isPending) ? 'Disconnecting...' : 'Unpublish & Disconnect Domain'}
              </Button>
            </div>
          ) : (
            <div className="flex max-w-[432px] w-full mx-auto items-center justify-end mt-8">
              <Button
                onClick={handleConnectSite}
                color="dark"
                className="px-6 py-2"
                disabled={addDomainMutation.isPending || connectUserDomainMutation.isPending}
              >
                {addDomainMutation.isPending || connectUserDomainMutation.isPending ? 'Connecting...' : 'Connect'}
              </Button>
            </div>
          )
          )}

        <div className="max-w-[896px] w-full mx-auto mt-8">
          <div className="h-px w-full bg-[#F2F2F3]" />
        </div>
          
        {/* Domain Information Table */}
        <div className="mt-4 overflow-x-auto max-w-[432px] w-full mx-auto">
          <Table className="w-full min-w-[320px] sm:min-w-[432px]">
            <TableHead>
              <TableRow>
                <TableHeader className="text-left text-sm !font-medium !text-[#71717B]">
                  Info
                </TableHeader>
                <TableHeader className="text-left text-sm !font-medium !text-[#71717B]">
                  Status
                </TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className="text-sm !font-normal !text-[#71717B]">Domain</TableCell>
                <TableCell className="text-sm !font-medium !text-[#09090B]">
                  {domainInfo?.name || domainName || '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-sm !font-normal !text-[#71717B]">Expiry date</TableCell>
                <TableCell className="text-sm !font-medium !text-[#09090B]">
                  {domainInfo?.expiry_date ? new Date(domainInfo.expiry_date).toLocaleDateString() : '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-sm !font-normal !text-[#71717B]">Connect date</TableCell>
                <TableCell className="text-sm !font-medium !text-[#09090B]">
                  {domainInfo?.created_at ? formatConnectDate(domainInfo.created_at) : formatConnectDate(new Date().toISOString())}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-sm !font-normal !text-[#71717B]">Registrar</TableCell>
                <TableCell className="text-sm !font-medium !text-[#09090B]">
                  {domainInfo?.domainType === 'withNS' ? 'CodeDesign' : 'Third Party'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-sm !font-normal !text-[#71717B]">DNS Record</TableCell>
                <TableCell className="text-sm !font-medium !text-[#09090B]">
                  {domainInfo?.dnsVerified ? 'Verified' : 'Not Verified'}
                  </TableCell>
                </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Already Connected Dialog */}
      <ProjectAlreadyConnectedDialog
        open={showAlreadyConnectedDialog}
        onClose={() => setShowAlreadyConnectedDialog(false)}
      />
    </div>
  )
}
